// client/src/components/TierUpgrade.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  CheckCircle,
  Lock,
  Star,
  ArrowUpward,
  Home,
  NavigateNext,
  EmojiEvents,
  CardMembership,
  Task as TaskIcon
} from '@mui/icons-material';

export default function TierUpgrade() {
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const navigate = useNavigate();

  // Fetch tier eligibility data
  const fetchTierData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/tiers/check-eligibility', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTierData(data);
      }
    } catch (err) {
      console.error('Failed to fetch tier data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle tier upgrade
  const handleUpgrade = async (targetTier) => {
    setUpgrading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/tiers/upgrade', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tier: targetTier })
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        fetchTierData(); // Refresh data
      } else {
        const error = await response.json();
        alert(error.error || 'Upgrade failed');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      alert('Failed to upgrade tier');
    } finally {
      setUpgrading(false);
    }
  };

  useEffect(() => {
    fetchTierData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Loading tier information...</Typography>
      </Box>
    );
  }

  if (!tierData) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load tier data
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', p: 2 }}>
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          <Home sx={{ mr: 0.5, fontSize: 20 }} />
          Dashboard
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <EmojiEvents sx={{ mr: 0.5, fontSize: 20 }} />
          Tier Status
        </Typography>
      </Breadcrumbs>

      {/* Back to Dashboard Button */}
      <Button
        variant="outlined"
        startIcon={<ArrowUpward sx={{ transform: 'rotate(-90deg)' }} />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      <Typography variant="h4" gutterBottom color="primary">
        <EmojiEvents sx={{ mr: 1, verticalAlign: 'middle' }} />
        Your Tier Status
      </Typography>
      
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Complete tasks to unlock higher tiers and earn more!
      </Typography>

      {/* Current Status Card - Updated to Green Theme */}
      <Card sx={{ 
        mb: 3, 
        background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
        color: 'white',
        boxShadow: 3,
        border: '2px solid #388E3C'
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <EmojiEvents sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              Current Tier: {tierData.current_tier}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
              <Star sx={{ fontSize: 18, verticalAlign: 'text-top', mr: 0.5 }} />
              <strong>{tierData.user_stats.tasks_completed}</strong> Tasks Completed
            </Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
              <Star sx={{ fontSize: 18, verticalAlign: 'text-top', mr: 0.5 }} />
              Rating: <strong>{tierData.user_stats.rating.toFixed(1)}/5</strong>
            </Typography>
          </Box>
          {tierData.current_tier !== 'Pro' && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              Keep completing tasks to unlock higher earning potential!
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Tier Cards */}
      {tierData.tiers.map((tier) => (
        <Card 
          key={tier.tier_name}
          sx={{ 
            mb: 3, 
            border: tier.is_current ? 2 : 0,
            borderColor: tier.is_current ? '#4CAF50' : 'primary.main',
            opacity: tier.is_locked ? 0.7 : 1,
            boxShadow: tier.is_current ? 3 : 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: 2,
              transform: tier.is_locked ? 'none' : 'translateY(-2px)'
            }
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h5" component="div" color="primary">
                  {tier.tier_name} Tier
                </Typography>
                <Chip 
                  label={tier.is_current ? 'Current Tier' : tier.is_eligible ? 'Eligible' : 'Locked'}
                  color={tier.is_current ? 'success' : tier.is_eligible ? 'primary' : 'default'}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>
              
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6" color="primary">
                  Up to £{tier.max_task_reward}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tier.max_daily_tasks === 999 ? 'Unlimited' : tier.max_daily_tasks} tasks/day
                </Typography>
              </Box>
            </Box>

            {/* Progress Bar */}
            {!tier.is_current && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Progress to {tier.tier_name}: {tier.user_progress.tasks_completed}/{tier.min_tasks_required} tasks
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(100, (tier.user_progress.tasks_completed / tier.min_tasks_required) * 100)}
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: '#f0f0f0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: tier.is_eligible ? '#4CAF50' : '#1976d2',
                      borderRadius: 5
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {tier.user_progress.tasks_remaining} more tasks needed • {tier.min_rating_required}+ rating required
                </Typography>
              </Box>
            )}

            {/* Features */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, color: 'primary.main' }}>
              Tier Benefits:
            </Typography>
            <List dense>
              {tier.features.map((feature, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {tier.is_current || tier.is_eligible ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : (
                      <Lock color="disabled" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText 
                    primary={feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: tier.is_current ? '#2E7D32' : 'inherit',
                        fontWeight: tier.is_current ? 600 : 'normal'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>

            {/* Upgrade Button */}
            {tier.is_eligible && !tier.is_current && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => handleUpgrade(tier.tier_name)}
                disabled={upgrading}
                startIcon={<ArrowUpward />}
                sx={{ 
                  mt: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)',
                    boxShadow: 3
                  },
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                {upgrading ? 'Upgrading...' : `Upgrade to ${tier.tier_name}`}
              </Button>
            )}

            {/* Requirements for locked tiers */}
            {tier.is_locked && (
              <Alert severity="info" sx={{ mt: 2, backgroundColor: '#E8F5E8' }}>
                <Typography variant="body2">
                  <strong>Requirements to unlock {tier.tier_name}:</strong><br />
                  • Complete {tier.min_tasks_required} tasks (currently at {tier.user_progress.tasks_completed})<br />
                  • Maintain {tier.min_rating_required}+ rating (currently {tier.user_progress.rating.toFixed(1)})
                </Typography>
              </Alert>
            )}

            {/* Current Tier Message */}
            {tier.is_current && tier.tier_name !== 'Pro' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>You're currently at the {tier.tier_name} tier!</strong><br />
                  Complete {tier.user_progress.tasks_remaining} more tasks to unlock the next tier.
                </Typography>
              </Alert>
            )}

            {/* Pro Tier Achievement Message */}
            {tier.is_current && tier.tier_name === 'Pro' && (
              <Alert severity="success" sx={{ mt: 2, backgroundColor: '#E8F5E8' }}>
                <Typography variant="body2">
                  <strong>🎉 Congratulations! You've reached the highest tier!</strong><br />
                  You're now earning maximum rewards with unlimited daily tasks.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Quick Navigation Section */}
      <Card sx={{ mt: 4, p: 2, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom color="primary">
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={() => navigate('/tasks')}
            startIcon={<TaskIcon />}
          >
            Browse Tasks
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/earnings')}
            startIcon={<Star />}
          >
            View Earnings
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/my-tasks')}
            startIcon={<CheckCircle />}
          >
            My Tasks
          </Button>
        </Box>
      </Card>
    </Box>
  );
}